import styled from 'styled-components';

import { PageSize } from 'ui/pageSize';
import { Pagination } from 'ui/pagination';
import { body2, colors, spacing } from 'ui/theme';
import noop from 'utils/noop';
import { PAGE_SIZES } from './constants';

const PaginationWrapper = styled.div`
  align-items: center;
  border-top: solid ${spacing.single} ${colors.grey15};
  display: grid;
  column-gap: ${spacing.base};
  padding: ${spacing.base} ${spacing.layoutLRPadding} 0 ${spacing.layoutLRPadding};
  color: ${colors.textPrimary};

  ${({ isItemCountShown }: { isItemCountShown: boolean }) =>
    isItemCountShown
      ? ` grid-template-columns: 2fr 5fr 2fr; `
      : ` grid-template-columns: 1fr 1fr; `}

  & {
    > :first-child {
      justify-self: start;
    }
    > :nth-child(2) {
      justify-self: center;
    }
    > :last-child {
      justify-self: end;
    }
  }
`;

const ItemCount = styled.div`
  ${body2}
`;

type ItemsPerPage = number;
type PageNumber = number;

export interface PaginationUpdateEvent {
  /** The number of visible rows shown on a page */
  pageSize: ItemsPerPage;
  /** The number the selected page */
  page: PageNumber;
}

export interface PaginationContent {
  /**
   * The suffix next to the total number of items.
   * EG: If there were 400 items and this was `total: 'rows'`, this would
   * say: '400 rows'.
   */
  total?: string;
}

export interface PaginationFooterProps {
  /** the index of the current active page */
  currentPage?: PageNumber;
  /** string content for the component */
  content?: PaginationContent;
  /** how many items to show on each fetch */
  itemsPerPage?: ItemsPerPage;
  /** the total number of pages to show */
  maxPages?: number;
  /** the total number of items in the data set */
  numItems?: number;
  /** A fn to run whenever the pagination changes; exposes data for use elsewhere */
  onUpdate?: (e: PaginationUpdateEvent) => void;
  /** an array of available set sizes for the view */
  pageSizes?: number[];
  /** Is the total number of items shown */
  isItemCountShown?: boolean;
}

export const paginationFooterContent = {
  total: 'total',
};

export const PaginationFooter = ({
  content = paginationFooterContent,
  currentPage = 1,
  isItemCountShown = true,
  itemsPerPage = PAGE_SIZES[0],
  maxPages = 5,
  numItems = 0,
  onUpdate = noop,
  pageSizes = PAGE_SIZES,
  ...props
}: PaginationFooterProps) => (
  <PaginationWrapper isItemCountShown={isItemCountShown} {...props}>
    {isItemCountShown && (
      <ItemCount data-test="pagination-footer-count">{`${numItems} ${content.total}`}</ItemCount>
    )}
    <Pagination
      page={currentPage}
      maxPages={maxPages}
      onChangePage={(newPage: number) => {
        onUpdate({
          pageSize: itemsPerPage,
          page: newPage,
        });
      }}
      pages={Math.ceil(numItems / itemsPerPage)}
    />
    <PageSize
      defaultSize={itemsPerPage}
      onSelect={(newPageSize: number) => {
        onUpdate({
          pageSize: newPageSize,
          page: 1,
        });
      }}
      pageSizes={pageSizes}
      total={numItems}
    />
  </PaginationWrapper>
);
